import { CircularProgress } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { GridApiPro, GridValidRowModel } from '@mui/x-data-grid-pro'

// Contexts
import { withTranslate } from '../../core/TenantProvider';
// Files in the same folder
import { ReactComponent as CloseIcon } from './assets/icon-close.svg';
import { ReactComponent as CopyIcon } from './assets/icon-copy.svg';
import { ReactComponent as AnchorIcon } from './assets/icon-anchor.svg';
import { getVbanCodeLabelMapping, VbanCodeLabel } from './mappingDefinition/codeLabelMappingDefinition';
import {
    IconSnackbarWrapper,
    LoadingSpinnerContainer,
    StyledSnackbar,
    VBANCodeCopy,
    VBANDetailsContent,
    VBANDetailsContentWrapper,
    VBANDetailsHeader,
    VBANDetailsWrapper
} from './styles/ClientManagementStyledComponents';
// Redux
import { ReducerState } from '../../redux/modules';
import { actions as ClientManagementActions } from './../../redux/modules/clientManagement';
import { ClientDetails, VBANDetails } from '../../redux/modules/clientManagement/reducer';
// Utils
import { TriggerGoogleAnalytics4Tag } from '../../core/utils/GoogleAnalyticTag';
import { GoogleAnalytics4Tags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
// Constants
import { VBAN_DETAILS_CODE_COPY_NOTIF_TIMER } from '../../core/utils/Constants/Constants';

interface VBANDetailFieldProps {
    label: string;
    value: string;
    fieldType: string;
    onCopy: Function;
    copiedDetail: string | null;
}

interface ClientVBANDetailsPanelProps {
    row: GridValidRowModel;
    rowId: string;
    apiRef: React.MutableRefObject<GridApiPro>;
    vbanDetailsLoadingState: string;
    vbanDetailsResult: VBANDetails;
    getVBANDetails: Function;
    clientDetailsResult: ClientDetails[];
    translate: any;
    forTestingCall? : boolean;
};

const VBANDetailField = ({ label, value, fieldType, onCopy, copiedDetail }: VBANDetailFieldProps) => (
    <VBANDetailsContent>
        <div>{label}</div>
        <VBANCodeCopy>
            <div>{value}</div>
            {value &&
                <IconSnackbarWrapper>
                    <CopyToClipboard text={value} onCopy={() => onCopy(fieldType, value)}>
                        <CopyIcon data-testid="ap-copy-code-icon" />
                    </CopyToClipboard>
                    <StyledSnackbar
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        open={copiedDetail === fieldType}
                        onClose={() => onCopy(null)}
                        autoHideDuration={VBAN_DETAILS_CODE_COPY_NOTIF_TIMER}
                    >
                        <div>
                            <AnchorIcon />
                            <div>
                                <span>Copied</span>
                            </div>
                        </div>
                    </StyledSnackbar>
                </IconSnackbarWrapper>}
        </VBANCodeCopy>
    </VBANDetailsContent >
);

const ClientVBANDetailsPanel = (props: ClientVBANDetailsPanelProps) => {
    const { row, rowId, apiRef, vbanDetailsLoadingState, vbanDetailsResult, getVBANDetails, translate, forTestingCall = false } = props;
    const { clientId } = row;

    const [copiedDetail, setCopiedDetail] = useState<string | null>(null);

    const rowIndex: number = apiRef.current.getRowIndexRelativeToVisibleRows(rowId);
    const isEvenRowIndex: boolean = rowIndex % 2 === 0;
    const vbanDetailsPanelHeader: string = translate('clientmanagement.vbanDetailsPanel.header')

    const vbanCodeLabelMapping: VbanCodeLabel[] = getVbanCodeLabelMapping(translate, vbanDetailsResult);

    useEffect(() => {
        !forTestingCall && getVBANDetails(clientId);
        TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.clientManagement.name,
            GoogleAnalytics4Tags.eventAction.click,
            GoogleAnalytics4Tags.clientManagement.click.viewDetailsButton
        );
    }, [getVBANDetails])

    const handleOnCopyCodeClick = (type: string) => {
        setCopiedDetail(type);
    };

    const handleClosePanelClick = () => {
        apiRef.current.toggleDetailPanel(rowId);
    };

    const isVbanDetailsLoading: boolean = vbanDetailsLoadingState === 'LOADING';

    return (
        <VBANDetailsWrapper isEvenRowIndex={isEvenRowIndex}>
            <VBANDetailsHeader>
                <div>{vbanDetailsPanelHeader}</div>
                <button onClick={handleClosePanelClick}><CloseIcon /></button>
            </VBANDetailsHeader>
            <VBANDetailsContentWrapper isEvenRowIndex={isEvenRowIndex}>
                {
                    isVbanDetailsLoading ?
                        (<LoadingSpinnerContainer>
                            <CircularProgress size={25} />
                        </LoadingSpinnerContainer>) :
                        (vbanCodeLabelMapping.map(({ label, value, fieldType }: VbanCodeLabel) => (
                            <VBANDetailField
                                key={fieldType}
                                label={label}
                                value={value}
                                fieldType={fieldType}
                                onCopy={handleOnCopyCodeClick}
                                copiedDetail={copiedDetail}
                            />
                        )))
                }
            </VBANDetailsContentWrapper>
        </VBANDetailsWrapper>
    );
};

const mapStateToProps = (state: ReducerState) => ({
    vbanDetailsLoadingState: state.clientManagement.vbanDetailsLoadingState,
    clientDetailsResult: state.clientManagement.clientDetailsResult,
    vbanDetailsResult: state.clientManagement.vbanDetailsResult
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getVBANDetails: ClientManagementActions.getVBANDetails
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withTranslate(
        (ClientVBANDetailsPanel)
    )
)